import type { ReactNode } from 'react';
import { useIsClient } from '@/hooks/useIsClient';

interface ClientOnlyProps {
  /** Browser-only content (reads `window`, measures layout, draws a map…). */
  children: ReactNode;
  /**
   * What the server and the hydration render show instead. Give it the size
   * the children will take, so nothing shifts when they arrive.
   */
  fallback?: ReactNode;
}

/**
 * Renders `children` only in the browser (web plan W4.2): `fallback` on the
 * server and while hydrating, the children from the next render on. In a tree
 * mounted by `createRoot` the children render straight away (see
 * `useIsClient`).
 *
 * Pair it with `React.lazy` for a module that touches `window` when it is
 * imported (Leaflet): the lazy import only runs when the children render,
 * which never happens on the server.
 */
export function ClientOnly({ children, fallback = null }: ClientOnlyProps) {
  return <>{useIsClient() ? children : fallback}</>;
}

export default ClientOnly;
